import React, { useMemo } from 'react';
import { Activity, TrendingUp, Target, Sparkles } from 'lucide-react';
import { computeApexScore, getApexScoreColor, APEX_SCORE_AXES } from '../lib/apexScore';
import { analyzeGame } from '../lib/gameInsights/index';
import { getAxisAdvice } from '../lib/axisAdvice';
import { useRankBenchmarks } from '../hooks/useRankBenchmarks';
import PlayerProfile from './PlayerProfile';

/**
 * APEX v8.1 — CoachingTab
 * Benchmarks chargés depuis Supabase rank_benchmarks.
 */
export default function CoachingTab({ games, userSettings }) {
  const allGames = Array.isArray(games) ? games : [];

  const { benchmarks, isFallback: benchmarkIsFallback, patch } = useRankBenchmarks(userSettings?.primary_role || 'ADC');

  const apex = useMemo(
    () => computeApexScore(allGames, userSettings, benchmarks),
    [allGames, userSettings, benchmarks]
  );

  const isFallback = benchmarkIsFallback || apex.isFallback;

  // Agrégation des insights sur les 20 dernières games
  const aggregatedInsights = useMemo(() => {
    const recent = [...allGames]
      .filter(g => !(g.isRemake === true))
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
      .slice(0, 20);

    const counter = {};
    const userStats = computeUserBaseline(allGames);

    for (const g of recent) {
      const insights = analyzeGame(g, { userStats, allGames });
      for (const ins of insights) {
        if (ins.severity === 'positive') continue; // on agrège les points à travailler
        if (!counter[ins.id]) {
          counter[ins.id] = { ...ins, count: 0 };
        }
        counter[ins.id].count++;
      }
    }

    return Object.values(counter)
      .sort((a, b) => b.count - a.count)
      .slice(0, 6);
  }, [allGames]);

  const colors = getApexScoreColor(apex.status);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-1 h-8 bg-gradient-to-b from-cyan-400 to-fuchsia-400" />
        <div>
          <div className="text-[10px] tracking-[0.4em] text-zinc-500 uppercase font-semibold">
            <Activity size={12} className="inline mr-1" /> Coaching
          </div>
          <div className="text-xs text-zinc-400 mt-1">
            Ton APEX Score, tes patterns récurrents et quoi travailler
          </div>
        </div>
      </div>

      {/* APEX Score full */}
      {apex.score === null ? (
        <div className="border border-zinc-800 bg-zinc-950/60 p-8 text-center text-zinc-500 text-sm">
          {apex.message}
        </div>
      ) : (
        <div className={`border ${colors.border} bg-gradient-to-br from-zinc-950 to-zinc-950/80`}>
          <div className="px-5 py-4 border-b border-zinc-800/60 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Activity size={16} className={colors.text} />
              <div className={`text-[11px] tracking-[0.3em] uppercase font-bold ${colors.text}`}>
                APEX Score — Climb Readiness
              </div>
            </div>
            <div className="text-[10px] text-zinc-500 font-mono">
              Sur {apex.sampleSize} games {isFallback ? '· benchmarks estimés' : `· benchmarks réels${patch ? ` patch ${patch}` : ''}`}
            </div>
          </div>

          <div className="px-5 py-3 border-b border-zinc-800/40 text-[11px] text-zinc-400 leading-relaxed">
            Ce score mesure <span className="text-zinc-200">si tu joues au niveau du tier au-dessus du tien</span> ({apex.targetTier}).
            100 = tu as déjà le niveau pour monter. C'est une moyenne pondérée de 7 axes
            comparés aux joueurs {apex.targetTier}. <span className="text-zinc-200">Vise 65+</span> :
            au-delà, statistiquement, tu es prêt à climb.
          </div>

          <div className="p-5 grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Score géant */}
            <div className="flex flex-col items-center justify-center">
              <div className={`text-6xl font-black ${colors.text}`} style={{ textShadow: `0 0 20px ${colors.glow}` }}>
                {apex.score}
              </div>
              <div className="text-sm text-zinc-500 font-mono">/ 100</div>
              <div className={`text-sm font-bold mt-2 text-center ${colors.text}`}>
                {apex.message}
              </div>
              {apex.targetTier && apex.status !== 'challenger_mastery' && (
                <div className="text-[10px] text-zinc-500 font-mono mt-1">
                  vs benchmark {apex.targetTier}
                </div>
              )}
            </div>

            {/* Breakdown 7 axes */}
            <div className="md:col-span-2 space-y-3">
              <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold mb-2">
                Breakdown par axe
              </div>
              {Object.entries(apex.axes).map(([key, axis]) => {
                const def = APEX_SCORE_AXES[key];
                const target = apex.benchmarkTargets?.[key];
                const axisColor = axis.score >= 65 ? 'bg-emerald-400'
                  : axis.score >= 40 ? 'bg-amber-400' : 'bg-rose-400';
                const axisTextColor = axis.score >= 65 ? 'text-emerald-400'
                  : axis.score >= 40 ? 'text-amber-400' : 'text-rose-400';

                // Formatage valeur actuelle selon l'axe
                const formatVal = (k, v) => {
                  if (v == null || isNaN(v)) return '—';
                  switch(k) {
                    case 'cs_per_min':      return v.toFixed(1);
                    case 'kda':             return v.toFixed(2);
                    case 'lane_wr_15':      return `${Math.round(v)}%`;
                    case 'damage_per_min':  return Math.round(v);
                    case 'vision':          return Math.round(v);
                    case 'deaths_per_game': return v.toFixed(1);
                    case 'win_rate':        return `${Math.round(v)}%`;
                    default: return Math.round(v);
                  }
                };

                return (
                  <div key={key}>
                    {/* Ligne header : label | toi vs cible | score/poids */}
                    <div className="flex items-center justify-between text-[11px] mb-1 gap-2">
                      <span className="text-zinc-300 font-semibold w-28 shrink-0">{def.label}</span>

                      {/* Valeur actuelle → cible */}
                      <div className="flex items-center gap-1.5 flex-1">
                        <span className={`font-mono font-bold ${axisTextColor}`}>
                          {formatVal(key, axis.value)}
                        </span>
                        {target != null && (
                          <>
                            <span className="text-zinc-700">→</span>
                            <span className="font-mono text-zinc-500">
                              {formatVal(key, target)}
                            </span>
                            <span className="text-zinc-700 text-[9px]">cible</span>
                          </>
                        )}
                      </div>

                      {/* Score + poids pill */}
                      <div className="flex items-center gap-1.5 shrink-0">
                        <span className={`font-mono font-bold text-[12px] ${axisTextColor}`}>
                          {Math.round(axis.score)}
                        </span>
                        <span className="text-zinc-600 text-[9px]">/100</span>
                        <span className="bg-zinc-800 border border-zinc-700 text-zinc-400 text-[9px] font-bold px-1.5 py-0.5 rounded-sm">
                          ×{def.weight.toFixed(2)}
                        </span>
                      </div>
                    </div>

                    {/* Barre de progression */}
                    <div className="h-1.5 bg-zinc-900 border border-zinc-800 overflow-hidden">
                      <div className={`h-full ${axisColor} transition-all duration-500`}
                        style={{ width: `${axis.score}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Recommandation enrichie : axe faible + ce qu'il faut viser + conseils */}
          {(() => {
            const weakest = Object.entries(apex.axes)
              .sort((a, b) => a[1].score - b[1].score)[0];
            if (!weakest) return null;
            const def = APEX_SCORE_AXES[weakest[0]];
            const advice = getAxisAdvice(weakest[0]);
            return (
              <div className="border-t border-zinc-800/60 bg-zinc-900/30 px-5 py-4">
                <div className="flex items-start gap-2 mb-3">
                  <TrendingUp size={14} className="text-cyan-400 mt-0.5 flex-shrink-0" />
                  <div className="text-[12px] text-zinc-300">
                    <span className="font-bold text-cyan-400">Prochain levier : {def.label}</span>{' '}
                    <span className="text-zinc-500">
                      ({Math.round(weakest[1].score)}/100 · pondéré {Math.round(def.weight * 100)}%)
                    </span>
                    {advice && (
                      <div className="text-zinc-400 mt-1">{advice.whatItMeasures}</div>
                    )}
                  </div>
                </div>
                {advice && (
                  <>
                    <div className="text-[11px] text-amber-300/90 mb-2 pl-6">
                      🎯 Objectif : {advice.target}
                    </div>
                    <div className="pl-6">
                      <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold mb-1.5">
                        Comment t'améliorer concrètement
                      </div>
                      <ul className="space-y-1">
                        {advice.tips.map((tip) => (
                          <li key={tip} className="text-[11px] text-zinc-400 flex gap-2">
                            <span className="text-cyan-500 flex-shrink-0">→</span>
                            <span>{tip}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </>
                )}
              </div>
            );
          })()}
        </div>
      )}

      {/* Profil joueur — radar + tags + forces/faiblesses (façon Porofessor) */}
      <div>
        <div className="text-[10px] tracking-[0.4em] text-cyan-400 uppercase font-semibold mb-3 flex items-center gap-2">
          <Sparkles size={12} /> Ton profil joueur
        </div>
        <PlayerProfile games={allGames} />
      </div>

      {/* Patterns récurrents — refondu : groupé, actionnable */}
      <div>
        <div className="text-[10px] tracking-[0.4em] text-fuchsia-400 uppercase font-semibold mb-3 flex items-center gap-2">
          <Target size={12} /> Patterns à corriger (20 dernières games)
        </div>
        {aggregatedInsights.length === 0 ? (
          <div className="border border-emerald-400/30 bg-emerald-400/5 p-4 text-sm text-emerald-300">
            ▲ Aucun pattern problématique récurrent détecté sur tes 20 dernières games. Continue comme ça.
          </div>
        ) : (
          <div className="space-y-2">
            {/* Top 3 patterns prioritaires mis en avant */}
            {aggregatedInsights.slice(0, 3).map((ins, idx) => {
              const sevColor = ins.severity === 'critical'
                ? 'border-rose-400/50 bg-rose-400/5'
                : ins.severity === 'warning'
                  ? 'border-amber-400/40 bg-amber-400/5'
                  : 'border-cyan-400/40 bg-cyan-400/5';
              const sevText = ins.severity === 'critical' ? 'text-rose-400'
                : ins.severity === 'warning' ? 'text-amber-400' : 'text-cyan-400';
              const freq = Math.round((ins.count / 20) * 100);
              return (
                <div key={ins.id} className={`border ${sevColor} p-3`}>
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-2">
                      <span className={`text-[9px] font-black px-1.5 py-0.5 border ${sevColor} ${sevText}`}>
                        #{idx + 1}
                      </span>
                      <span className={`font-bold text-[12px] ${sevText}`}>{ins.title}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-16 h-1 bg-zinc-800 overflow-hidden">
                        <div className={sevText.replace('text-', 'bg-')} style={{ width: `${freq}%`, height: '100%' }} />
                      </div>
                      <span className="text-[10px] font-mono text-zinc-500 w-14 text-right">
                        {ins.count}/20
                      </span>
                    </div>
                  </div>
                  <div className="text-[11px] text-zinc-400 leading-relaxed">{ins.long}</div>
                </div>
              );
            })}

            {/* Patterns secondaires condensés */}
            {aggregatedInsights.length > 3 && (
              <div className="border border-zinc-800 bg-zinc-950 p-3">
                <div className="text-[9px] uppercase tracking-widest text-zinc-600 font-bold mb-2">
                  Autres patterns mineurs
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {aggregatedInsights.slice(3).map(ins => (
                    <span key={ins.id} className="text-[10px] text-zinc-500 border border-zinc-800 px-2 py-1">
                      {ins.title} <span className="text-zinc-700">·{ins.count}x</span>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Calcule une baseline simple de l'user pour alimenter les comparaisons "vs TOI".
 */
function computeUserBaseline(allGames) {
  const valid = (allGames || []).filter(g =>
    !(g.isRemake === true) &&
    !(typeof g.duration === 'number' && g.duration > 0 && g.duration < 5)
  );
  if (valid.length === 0) return {};

  const greedTotal = valid.reduce((s, g) =>
    s + (g.deathDetails || []).filter(d => d?.type === 'greed').length, 0);
  const visionTotal = valid.reduce((s, g) => s + (g.myStats?.visionScore || 0), 0);
  const wardsTotal = valid.reduce((s, g) => s + (g.myStats?.wardsPlaced || 0), 0);

  return {
    avgGreedPerGame: greedTotal / valid.length,
    avgVisionScore: visionTotal / valid.length,
    avgWardsPlaced: wardsTotal / valid.length
  };
}
